import {
    Button,
    Divider,
    InputNumber,
    message,
    notification,
    Space,
    Typography,
} from 'antd';
import { get } from 'lodash';
import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AiFillEdit } from 'react-icons/ai';
import { FaLightbulb } from 'react-icons/fa';
import { updateSmallFleet } from '../../../api/small-fleet.api';
import { PageHeader } from '../../PageHeader';
import { useWorkspaceStore } from '../../../store/workspace.store';
import { generateDataToUpdate } from '../utils/account';

const { Title, Text } = Typography;

const CoverageConfig: FC = () => {
    const { t } = useTranslation('settings', { keyPrefix: 'SETTINGS' });
    const { t: ta } = useTranslation('shared', { keyPrefix: 'ACTIONS' });

    const workspace = useWorkspaceStore((state) => state.data);

    const [limit] = useState<number>(31);
    const [loading, setLoading] = useState<boolean>(false);
    const [value, setValue] = useState<number | null>(workspace?.coverageDistance || null);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const onFinish = async () => {
        setLoading(true);

        const dataToUpdate = await generateDataToUpdate({
            formValues: {
                coverageDistance: value,
            },
            activeWorkspace: workspace,
            defaultLogo: workspace?.image || '',
            isLogoUploaded: null,
            setLogoUploaded: () => { },
        });

        const fleetId = workspace?._id || '';

        updateSmallFleet(fleetId, dataToUpdate)
            .then(() => {
                notification.success({
                    message: t('GENERAL.DATA_UPDATED_SUCCESS'),
                });
            })
            .catch((error) => {
                const description = get(error, 'response.data.message', t('GENERAL.DATA_UPDATED_ERROR'));
                notification.error({
                    message: 'Error',
                    description,
                });
            })
            .finally(() => {
                setIsEditing(!isEditing);
                setLoading(false);
            });
    };

    return (
        <PageHeader
            title={(
                <Title level={5} style={{ marginBottom: 0 }}>
                    {t('GENERAL.COVERAGE_CONFIG')}
                </Title>
            )}
            footer={(
                <div
                    style={{
                        borderRadius: 8,
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
                        padding: '16px 24px',
                    }}
                >
                    <Space>
                        <Text>
                            {t('GENERAL.MAXIMUM_DELIVERY_DISTANCE')}
                        </Text>
                        <InputNumber
                            min={1}
                            max={limit}
                            value={value}
                            addonAfter="km"
                            readOnly={!isEditing}
                            size="small"
                            style={{ width: 100 }}
                            onChange={(value) => {
                                if (value === limit) {
                                    message.error(t('GENERAL.MAXIMUM_DELIVERY_DISTANCE_ERROR'));
                                    return;
                                }
                                setValue(value);
                            }}
                        />
                        {
                            !isEditing ? (
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<AiFillEdit size={18} style={{ verticalAlign: 'middle' }} />}
                                    onClick={() => setIsEditing(!isEditing)}
                                />
                            ) : (
                                <Button
                                    type="primary"
                                    loading={loading}
                                    size="small"
                                    onClick={() => onFinish()}
                                >
                                    {ta('SAVE')}
                                </Button>

                            )
                        }
                    </Space>

                    <Divider style={{ margin: '16px 0' }} />

                    <Space size={8}>
                        <FaLightbulb size={16} style={{ verticalAlign: 'middle' }} />
                        <Text>
                            <Trans
                                t={t}
                                i18nKey="GENERAL.MAXIMUM_DELIVERY_DISTANCE_HELP"
                                components={{ b: <b /> }}
                            />
                        </Text>
                    </Space>
                </div>
            )}
        />

    );
};

export { CoverageConfig };
